import { useMutation, useQueryClient } from "@tanstack/react-query";

import { errorHandler } from "#app/monitoring/errorHandler";
import { showErrorToast } from "#app/notifications";
import { useCompany } from "#modules/company/view/useCompany";
import { isOptimizationInvalidationCancelled } from "#modules/optimizations/infra/optimizationInvalidationConfirmation";
import { QuoteRequestLineDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLine.api";
import { invalidateQuoteRequestLinesDebounced } from "#modules/quoteRequestLines/infra/invalidateQuoteRequestLinesDebounced";
import {
  getQuoteRequestLineQueryData,
  quoteRequestLineQuery,
  setQuoteRequestLineQueryData,
} from "#modules/quoteRequestLines/infra/quoteRequestLine.query";
import { updateMaterial } from "#modules/quoteRequestLines/updateMaterial.api";
import { AnalyticsEvent, useAnalytics } from "#shared/useAnalytics";
import { JobStatusSchema, JobStatusType } from "shared";

type SentData = Parameters<typeof updateMaterial>[0];

type Options = {
  onSuccess?: (
    quoteRequestLines: { id: string; quoteRequestId: string; partId: string }[],
    sentData: SentData,
  ) => void;
  onMutate?: () => void;
  trigger?: string;
};

const updateMaterialFn = ({
  predictionStatus: _predictionStatus,
  ...sentData
}: SentData & { predictionStatus: JobStatusType | null }) => {
  return updateMaterial({
    quoteRequestLineId: sentData.quoteRequestLineId,
    payload: sentData.payload,
  });
};

export const useUpdateMaterial = (options?: Options) => {
  const queryClient = useQueryClient();
  const analytics = useAnalytics();
  const { company } = useCompany();
  const { mutateAsync, ...rest } = useMutation({
    mutationFn: updateMaterialFn,
    onMutate: async ({ quoteRequestLineId, predictionStatus, payload }) => {
      if (predictionStatus === JobStatusSchema.enum.PROCESSED) {
        analytics.capture(AnalyticsEvent.PredictionRerun, {
          quote_request_line_id: quoteRequestLineId,
          trigger: options?.trigger ?? "updateMaterial",
          company_id: company?.id ?? null,
          company_name: company?.name ?? null,
        });
      }
      options?.onMutate?.();

      await queryClient.cancelQueries({
        queryKey: quoteRequestLineQuery({ id: quoteRequestLineId }).queryKey,
      });

      const previousQuoteRequestLine = getQuoteRequestLineQueryData(
        queryClient,
        quoteRequestLineId,
      );

      if (previousQuoteRequestLine) {
        setQuoteRequestLineQueryData(queryClient, quoteRequestLineId, {
          ...previousQuoteRequestLine,
          ...payload,
          predictionStatus: "STANDBY",
        });
      }

      return { previousQuoteRequestLine, quoteRequestLineId };
    },

    onSuccess: (response, sentData) => {
      if (!response?.updatedQuoteRequestLines) {
        return;
      }

      response.updatedQuoteRequestLines.forEach((quoteRequestLine) => {
        const previousQuoteRequestLine = getQuoteRequestLineQueryData(
          queryClient,
          quoteRequestLine.id,
        );

        if (!previousQuoteRequestLine) {
          return;
        }

        setQuoteRequestLineQueryData(queryClient, quoteRequestLine.id, {
          ...previousQuoteRequestLine,
          materialName: quoteRequestLine.materialName,
          materialGrade: quoteRequestLine.materialGrade,
        });

        invalidateQuoteRequestLinesDebounced({
          queryClient,
          quoteRequestLineId: quoteRequestLine.id,
        });
      });

      options?.onSuccess?.(response.updatedQuoteRequestLines, sentData);
    },

    onError: (
      err: Error,
      _sentData,
      context?: {
        previousQuoteRequestLine?: QuoteRequestLineDto;
        quoteRequestLineId: string;
      },
    ) => {
      if (context?.previousQuoteRequestLine) {
        setQuoteRequestLineQueryData(
          queryClient,
          context.quoteRequestLineId,
          context.previousQuoteRequestLine,
        );
      }

      if (isOptimizationInvalidationCancelled(err)) {
        return;
      }

      showErrorToast({
        title: err.message,
      });
      errorHandler(err);
    },
  });

  return { updateMaterial: mutateAsync, ...rest };
};
